import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { londonToday, renewalFlag, renewalState } from '@/lib/sla'
import { formatDate } from '@/lib/dates'
import { useSlaSettings } from '@/queries/settings'
import { RenewalBadge } from './RenewalBadge'
import { clientTabPath } from './tabs'

/**
 * A client's renewal flag linking to its Contract & SLA tab. Options: showDate gives the plain end date before it is
 * flagged, showNone says "No SLA" for a client without settings, compact words the flag for a column.
 */
export function RenewalPill({
  companyId,
  showDate = false,
  showNone = false,
  compact = false,
  empty = null,
}: {
  companyId: string
  showDate?: boolean
  showNone?: boolean
  compact?: boolean
  empty?: ReactNode
}) {
  const settings = useSlaSettings()
  if (settings.isError) return showDate ? <span className="text-etn-text-muted">Unavailable</span> : null
  const sla = settings.data?.data.find(row => row.companyId === companyId)?.sla
  const state = renewalState(sla, londonToday())
  const tab = clientTabPath(companyId, 'contract-sla') as never
  if (renewalFlag(state)) {
    return (
      <Link to={tab} className="inline-flex">
        <RenewalBadge state={state} slasOff={!sla?.enabled} compact={compact} />
      </Link>
    )
  }
  if (showNone && settings.data && !sla) {
    return (
      <Link to={tab} className="inline-flex">
        <Badge variant="neutral">No SLA</Badge>
      </Link>
    )
  }
  if (showDate && sla?.endDate) return <>{formatDate(sla.endDate)}</>
  return <>{empty}</>
}
